import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Camera } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtNum } from "@/lib/format";
import { PageHeader, StatusBadge, NativeSelect, LoadingBlock, EmptyState } from "@/components/Shared";

export default function History() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [projects, setProjects] = useState([]);
  const [f, setF] = useState({ start: "", end: "", project_id: "" });
  const { user } = useAuth();

  useEffect(() => { api.get("/form-options").then((r) => setProjects(r.data.projects)); }, []);
  useEffect(() => {
    (async () => {
      const params = { page, limit: 20, mine: true, shared: true, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) };
      const { data } = await api.get("/reports", { params });
      setItems((prev) => (page === 1 ? data.items : [...(prev || []), ...data.items]));
      setTotal(data.total);
    })();
  }, [f, page]);

  const set = (k, v) => { setPage(1); setF({ ...f, [k]: v }); };

  return (
    <div className="fade-up">
      <PageHeader eyebrow="My projects" title="Report history" />
      <div className="panel mb-4 grid grid-cols-2 gap-2 p-3 sm:grid-cols-3">
        <input type="date" className="field-input" value={f.start} onChange={(e) => set("start", e.target.value)} data-testid="history-filter-start" aria-label="From date" />
        <input type="date" className="field-input" value={f.end} onChange={(e) => set("end", e.target.value)} data-testid="history-filter-end" aria-label="To date" />
        <NativeSelect className="col-span-2 sm:col-span-1" value={f.project_id} onChange={(e) => set("project_id", e.target.value)} data-testid="history-filter-project" aria-label="Filter by project">
          <option value="">All projects</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </NativeSelect>
      </div>
      {!items ? <LoadingBlock /> : !items.length ? (
        <EmptyState title="No reports found" text="Your reports, drafts, and reports others filed on your projects show up here." testId="history-empty" />
      ) : (
        <div className="space-y-2 stagger" data-testid="history-list">
          {items.map((r) => (
            <Link key={r.id} to={r.status === "draft" ? `/reports/${r.id}/edit` : `/reports/${r.id}`} data-testid={`history-item-${r.id}`}
              className="panel flex items-center gap-3 p-4 transition-colors hover:border-[#5C241E]">
              <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-md bg-[#5C241E]/5 text-[#5C241E]">
                <FileText className="h-5 w-5" />
                <span className="text-[10px] font-bold">#{r.report_number ?? "—"}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-slate-900">{r.project_name}</p>
                <p className="text-xs text-slate-600">{fmtDate(r.report_date)} · {r.client_name}</p>
                {r.employee_id !== user?.id && <p className="text-xs font-semibold text-[#5C241E]" data-testid={`history-filed-by-${r.id}`}>Filed by {r.employee_name}</p>}
                <p className="mt-0.5 flex items-center gap-2 text-xs text-slate-500"><Camera className="h-3 w-3" />{r.photo_count} · {r.total_hours != null ? `${fmtNum(r.total_hours)} hrs` : "no hours"}</p>
              </div>
              <StatusBadge status={r.status} />
            </Link>
          ))}
          {items.length < total && (
            <button className="btn-ghost h-12 w-full" onClick={() => setPage(page + 1)} data-testid="history-load-more">Load more</button>
          )}
        </div>
      )}
    </div>
  );
}
